import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Form,
  Label,
  Input,
  Spinner,
  Progress
} from "reactstrap";
import IntlMessages from "Util/IntlMessages";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { SuitePageShell } from "../../_suite/primitives";

export default function SlaSettingsPage() {
  const { data, loading, error, reload } = useAsyncResource(
    () => suiteApi.ticketing.getSla(),
    []
  );
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting }
  } = useForm();

  useEffect(() => {
    if (data) reset(data);
  }, [data, reset]);

  const first = watch("firstResponseMins");
  const resolution = watch("resolutionMins");

  async function onSubmit(values: Record<string, string>) {
    await suiteApi.ticketing.saveSla(values);
    reload();
  }

  const fr = Number(first) || 0;
  const res = Number(resolution) || 0;
  const demoProgress = res ? Math.min(100, Math.round((fr / res) * 100)) : 0;

  return (
    <SuitePageShell heading={<IntlMessages id="suite.ticketing.sla" />}>
      {loading && !data && (
        <Spinner className="mb-3" />
      )}
      {error && <p className="text-danger">{error}</p>}
      {data && (
        <Row>
          <Col lg="7" className="mb-4">
            <Card className="border-0 shadow-sm">
              <CardBody>
                <CardTitle tag="h5" className="mb-3">
                  Policy targets
                </CardTitle>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  <div className="mb-3">
                    <Label>
                      <IntlMessages id="suite.ticketing.sla-first" />
                    </Label>
                    <Input type="number" {...register("firstResponseMins")} />
                  </div>
                  <div className="mb-3">
                    <Label>
                      <IntlMessages id="suite.ticketing.sla-resolution" />
                    </Label>
                    <Input type="number" {...register("resolutionMins")} />
                  </div>
                  <div className="mb-4">
                    <Label>
                      <IntlMessages id="suite.ticketing.sla-hours" />
                    </Label>
                    <Input type="text" {...register("businessHours")} />
                  </div>
                  <Button color="primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? <Spinner size="sm" /> : null}{" "}
                    <IntlMessages id="suite.action.save" />
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
          <Col lg="5" className="mb-4">
            <Card className="border-0 shadow-sm bg-light">
              <CardBody>
                <CardTitle tag="h5" className="mb-3">
                  SLA preview
                </CardTitle>
                <p className="small text-muted">
                  Relative share of first-response vs resolution window (demo
                  visualization).
                </p>
                <div className="mb-2">
                  <div className="d-flex justify-content-between small">
                    <span>First response window</span>
                    <span>{fr || "—"} min</span>
                  </div>
                  <Progress value={demoProgress} color="info" className="mb-3" />
                </div>
                <div className="mb-2">
                  <div className="d-flex justify-content-between small">
                    <span>Resolution target</span>
                    <span>{res || "—"} min</span>
                  </div>
                  <Progress value={100} color="success" />
                </div>
                <hr />
                <p className="small mb-0">
                  <strong>Business hours:</strong>{" "}
                  {watch("businessHours") || "—"}
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </SuitePageShell>
  );
}
